import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineAlert } from "src/grading/components/InlineAlert/InlineAlert";
import LoadingButton from "src/grading/components/LoadingButton/LoadingButton";
import { SourceBadge } from "src/grading/components/SourceBadge/SourceBadge";
import {
  messageOf,
  refusalCode,
  refusalCopy,
} from "src/grading/utils/apiError";
import { ApiError, BenchmarkSex } from "src/models";
import { UpdateStudentPayload } from "src/models/hierarchy";
import {
  STUDENT_GRADES,
  StudentDetail,
  StudentField,
} from "src/models/students";
import { useUpdateStudentMutation } from "src/store/reducers/classes/classesApiSlice";

export interface StudentRecordFormProps {
  student: StudentDetail;
  /** Shows every field as text and hides Save (platform admins, viewers without update rights). */
  readOnly?: boolean;
  /** Schools an admin may move a school-added student to; the field shows when `schoolId` is editable. */
  schools?: { id: string; name: string }[];
  /** The class page the form sits on, for refreshing its roster. */
  classId?: string;
  /** Reloads the record after a `STALE_RECORD` refusal. */
  onReload?: () => void;
  onSaved?: (student: StudentDetail) => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  benchmarkSex: BenchmarkSex | "";
  birthDate: string;
  ageOverride: string;
  gradeLevel: string;
  externalId: string;
  schoolId: string;
  videoOptOut: boolean;
  photoOptOut: boolean;
}

const fromStudent = (student: StudentDetail): FormState => ({
  firstName: student.firstName,
  lastName: student.lastName,
  benchmarkSex: student.benchmarkSex ?? "",
  birthDate: student.birthDate ?? "",
  ageOverride:
    student.ageOverride !== undefined && student.ageOverride !== null
      ? String(student.ageOverride)
      : "",
  gradeLevel: student.gradeLevel ?? "",
  externalId: student.externalId ?? "",
  schoolId: student.schoolId,
  videoOptOut: student.consentFlags.videoOptOut,
  photoOptOut: student.consentFlags.photoOptOut,
});

/** A read-only field: its label, its value and, for roster-owned fields, the Roster badge. */
const LockedField = ({
  label,
  value,
  roster,
  rosterLabel,
}: {
  label: string;
  value: ReactNode;
  roster: boolean;
  /** Read after the value instead of the badge ("Aiden, from the roster"). */
  rosterLabel: string;
}) => (
  // A <dl> may wrap each <dt>/<dd> pair in one <div> and nothing else, so the Roster badge sits
  // inside the <dd>, next to the value it describes.
  <Box sx={{ py: 0.75 }}>
    <Box
      component="dt"
      sx={(theme) => ({
        fontSize: 12,
        fontWeight: 700,
        color: theme.palette.tokens.textMuted,
      })}
    >
      {label}
    </Box>
    <Box
      component="dd"
      sx={{
        m: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        fontSize: 15,
        fontWeight: 700,
      }}
    >
      <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {value}
      </Box>
      {roster ? (
        <>
          <Box component="span" aria-hidden="true">
            <SourceBadge source="roster" />
          </Box>
          <Box component="span" sx={visuallyHidden}>
            {`, ${rosterLabel.toLocaleLowerCase()}`}
          </Box>
        </>
      ) : null}
    </Box>
  </Box>
);

/**
 * The student record with only the allowed fields (FR-027). Fields the server reports in
 * `readOnlyFields` render as text; for a rostered student they carry the Roster badge and the
 * note that the district's student information system owns them (FR-022). Saves send
 * `expectedUpdatedAt`, so a concurrent change comes back as `STALE_RECORD`.
 */
export const StudentRecordForm = ({
  student,
  readOnly = false,
  schools,
  classId,
  onReload,
  onSaved,
}: StudentRecordFormProps) => {
  const { t } = useTranslation();
  const uid = useId();
  const [updateStudent, { isLoading: saving }] = useUpdateStudentMutation();
  const [form, setForm] = useState<FormState>(() => fromStudent(student));
  const [notice, setNotice] = useState<{
    severity: "success" | "error";
    text: string;
  } | null>(null);
  const [stale, setStale] = useState(false);

  // Reload the form only when the record's version changes, so a refetch keeps unsaved typing.
  const loadedVersion = useRef(`${student.id}:${student.updatedAt}`);
  useEffect(() => {
    const version = `${student.id}:${student.updatedAt}`;
    if (loadedVersion.current === version) return;
    loadedVersion.current = version;
    setForm(fromStudent(student));
    setStale(false);
  }, [student]);

  const roster = student.source === "roster";
  const locked = (field: StudentField) =>
    readOnly || student.readOnlyFields.includes(field);
  const notSet = t("review.notSet");
  const showsBirthDate = student.birthDate !== undefined;
  const ageField: StudentField = showsBirthDate ? "birthDate" : "ageOverride";
  const schoolOptions = schools ?? [];
  const showSchool =
    !readOnly &&
    schoolOptions.length > 1 &&
    student.allowedActions.edit.includes("schoolId");

  const shownFields: StudentField[] = [
    "firstName",
    "lastName",
    "benchmarkSex",
    ageField,
    "gradeLevel",
    "externalId",
  ];
  const rosterLocked =
    roster &&
    shownFields.some((field) => student.readOnlyFields.includes(field));

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const changes = (): UpdateStudentPayload => {
    const before = fromStudent(student);
    const body: UpdateStudentPayload = {};
    const text = (key: "firstName" | "lastName") => form[key].trim();
    if (!locked("firstName") && text("firstName") !== before.firstName) {
      body.firstName = text("firstName");
    }
    if (!locked("lastName") && text("lastName") !== before.lastName) {
      body.lastName = text("lastName");
    }
    if (!locked("benchmarkSex") && form.benchmarkSex !== before.benchmarkSex) {
      body.benchmarkSex = form.benchmarkSex === "" ? null : form.benchmarkSex;
    }
    if (showsBirthDate) {
      if (!locked("birthDate") && form.birthDate !== before.birthDate) {
        body.birthDate = form.birthDate || null;
      }
    } else if (
      !locked("ageOverride") &&
      form.ageOverride !== before.ageOverride
    ) {
      body.ageOverride = form.ageOverride ? Number(form.ageOverride) : null;
    }
    if (!locked("gradeLevel") && form.gradeLevel !== before.gradeLevel) {
      body.gradeLevel = form.gradeLevel || null;
    }
    if (!locked("externalId") && form.externalId.trim() !== before.externalId) {
      body.externalId = form.externalId.trim() || null;
    }
    if (showSchool && form.schoolId !== before.schoolId) {
      body.schoolId = form.schoolId;
    }
    if (
      !locked("consentFlags") &&
      (form.videoOptOut !== before.videoOptOut ||
        form.photoOptOut !== before.photoOptOut)
    ) {
      body.consentFlags = {
        videoOptOut: form.videoOptOut,
        photoOptOut: form.photoOptOut,
      };
    }
    return body;
  };

  const pending = changes();
  const dirty = Object.keys(pending).length > 0;
  const namesMissing =
    (!locked("firstName") && !form.firstName.trim()) ||
    (!locked("lastName") && !form.lastName.trim());

  const save = async () => {
    setNotice(null);
    setStale(false);
    try {
      const saved = await updateStudent({
        studentId: student.id,
        classId,
        body: { ...pending, expectedUpdatedAt: student.updatedAt },
      }).unwrap();
      setNotice({ severity: "success", text: t("student.saved") });
      onSaved?.(saved);
    } catch (error) {
      const code = refusalCode(error);
      if (code === "STALE_RECORD") {
        setStale(true);
        return;
      }
      if (code === "ROSTER_MANAGED") {
        const fields = (error as ApiError).data?.data?.fields;
        const named = Array.isArray(fields)
          ? fields
              .map((field) => t(`student.field.${String(field)}`))
              .join(", ")
          : "";
        setNotice({
          severity: "error",
          text: named
            ? `${refusalCopy(t, code)} ${t("student.lockedFields", { fields: named })}`
            : refusalCopy(t, code),
        });
        return;
      }
      setNotice({
        severity: "error",
        text: code
          ? refusalCopy(t, code)
          : messageOf(error, t("student.saveError")),
      });
    }
  };

  const sexLabel = (value: BenchmarkSex | null) =>
    value ? t(`classes.sex${value}`) : notSet;
  const lockedRows: ReactNode[] = [];
  const editRows: ReactNode[] = [];
  const place = (
    field: StudentField,
    label: string,
    value: ReactNode,
    editor: ReactNode,
  ) => {
    if (locked(field)) {
      lockedRows.push(
        <LockedField
          key={field}
          label={label}
          value={value}
          roster={roster && student.readOnlyFields.includes(field)}
          rosterLabel={t("student.fromRoster")}
        />,
      );
    } else {
      editRows.push(<Box key={field}>{editor}</Box>);
    }
  };

  place(
    "firstName",
    t("classes.firstName"),
    student.firstName,
    <TextField
      fullWidth
      size="small"
      label={t("classes.firstName")}
      value={form.firstName}
      onChange={(event) => set("firstName", event.target.value)}
      inputProps={{ maxLength: 80 }}
      required
    />,
  );
  place(
    "lastName",
    t("classes.lastName"),
    student.lastName,
    <TextField
      fullWidth
      size="small"
      label={t("classes.lastName")}
      value={form.lastName}
      onChange={(event) => set("lastName", event.target.value)}
      inputProps={{ maxLength: 80 }}
      required
    />,
  );
  place(
    "benchmarkSex",
    t("classes.benchmarkTable"),
    sexLabel(student.benchmarkSex),
    <TextField
      select
      fullWidth
      size="small"
      label={t("classes.benchmarkTable")}
      value={form.benchmarkSex}
      onChange={(event) =>
        set("benchmarkSex", event.target.value as BenchmarkSex | "")
      }
      helperText={
        student.benchmarkSexSource === "school"
          ? t("student.sexSetBySchool")
          : roster
            ? t("student.sexFillIn")
            : undefined
      }
    >
      <MenuItem value="">{notSet}</MenuItem>
      <MenuItem value="Male">{t("classes.sexMale")}</MenuItem>
      <MenuItem value="Female">{t("classes.sexFemale")}</MenuItem>
    </TextField>,
  );
  if (showsBirthDate) {
    place(
      "birthDate",
      t("student.birthDate"),
      student.birthDate || notSet,
      <TextField
        fullWidth
        size="small"
        type="date"
        label={t("student.birthDate")}
        value={form.birthDate}
        onChange={(event) => set("birthDate", event.target.value)}
        InputLabelProps={{ shrink: true }}
      />,
    );
  } else {
    place(
      "ageOverride",
      t("classes.age"),
      student.ageYears ?? notSet,
      <TextField
        fullWidth
        size="small"
        type="number"
        label={t("classes.age")}
        value={form.ageOverride}
        onChange={(event) => set("ageOverride", event.target.value)}
        inputProps={{ min: 5, max: 19 }}
        helperText={t("classes.ageHelper")}
      />,
    );
  }
  place(
    "gradeLevel",
    t("classes.grade"),
    student.gradeLevel ?? notSet,
    <TextField
      select
      fullWidth
      size="small"
      label={t("classes.grade")}
      value={form.gradeLevel}
      onChange={(event) => set("gradeLevel", event.target.value)}
    >
      <MenuItem value="">{notSet}</MenuItem>
      {STUDENT_GRADES.map((grade) => (
        <MenuItem key={grade} value={grade}>
          {grade}
        </MenuItem>
      ))}
    </TextField>,
  );
  place(
    "externalId",
    t("classes.sisId"),
    student.externalId || "—",
    <TextField
      fullWidth
      size="small"
      label={t("classes.sisId")}
      value={form.externalId}
      onChange={(event) => set("externalId", event.target.value)}
      inputProps={{ maxLength: 100 }}
    />,
  );

  const consentLocked = locked("consentFlags");

  return (
    <Box
      component="form"
      aria-label={t("student.formLabel")}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!readOnly && dirty && !namesMissing) void save();
      }}
    >
      <Stack spacing={2}>
        {lockedRows.length ? (
          <Box>
            {rosterLocked ? (
              <Typography
                id={`${uid}-roster-note`}
                sx={(theme) => ({
                  fontSize: 13,
                  mb: 0.5,
                  color: theme.palette.tokens.textMuted,
                })}
              >
                {t("student.rosterManaged")}
              </Typography>
            ) : null}
            <Box
              component="dl"
              aria-describedby={rosterLocked ? `${uid}-roster-note` : undefined}
              sx={{ m: 0 }}
            >
              {lockedRows}
            </Box>
          </Box>
        ) : null}
        {editRows}
        {showSchool ? (
          <TextField
            select
            fullWidth
            size="small"
            label={t("student.school")}
            value={form.schoolId}
            onChange={(event) => set("schoolId", event.target.value)}
            helperText={t("student.schoolMoveHelper")}
          >
            {schoolOptions.map((school) => (
              <MenuItem key={school.id} value={school.id}>
                {school.name}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.videoOptOut}
                onChange={(event) => set("videoOptOut", event.target.checked)}
                disabled={consentLocked}
              />
            }
            label={t("student.videoOptOut")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.photoOptOut}
                onChange={(event) => set("photoOptOut", event.target.checked)}
                disabled={consentLocked}
              />
            }
            label={t("student.photoOptOut")}
          />
        </Box>
        {stale ? (
          <InlineAlert
            severity="warning"
            action={
              onReload ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setStale(false);
                    onReload();
                  }}
                >
                  {t("student.reload")}
                </Button>
              ) : undefined
            }
          >
            {refusalCopy(t, "STALE_RECORD")}
          </InlineAlert>
        ) : null}
        {notice ? (
          <InlineAlert severity={notice.severity}>{notice.text}</InlineAlert>
        ) : null}
        {readOnly ? null : (
          <Box>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={saving}
              disabled={!dirty || namesMissing}
              label={t("common.save")}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default StudentRecordForm;
